import {
  fetchCallCenterDevices,
  fetchSentDevicePatientReturned,
  handleMarkDevicePatientReturned,
  checkCanShowModal,
  checkDeviceError,
  getValueAmpEcg,
  handleConvertDeviceData,
  type DeviceOption,
  DEVICE_STATUS,
} from '@/entities/device';
import { useUserStore } from '@/entities/user';
import { useLoadingStore } from '@/shared/model';
import {
  CARDIO_ONE_DEVICE_MODEL_LIST,
  DEVICE,
  DEVICE_MODEL_LIST,
  EMITTER_CONSTANTS,
  LOADING_TYPES,
  ORGANIZATION_SLUG,
  ROLE,
} from '@/shared/config';
import { STUDY_TYPES } from '@/entities/study';
import { auth, isWebViewCreateStudy, showMessageError } from '@/shared/lib';
import { useEmitter, useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import type { DeviceEventSocketMessage, StudyPricingUpdatedSocketMessage } from '@/shared/lib';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { t } from 'ttag';
import type { AssignDeviceDrawerProps, AssignDeviceDrawerState } from './assignDeviceDrawer.types';

/**
 * Drives the drawer that assigns a device to a study: model choice, device search and selection,
 * warning and error modals, and the assign submit. Loads default devices whenever the drawer opens
 * or the model changes, debounces device-ID searches by 500 ms, resets the selection on close, and
 * keeps the device list in step with study-pricing and device-event socket messages.
 *
 * @param props - The drawer's visible flag, the handleAssignDeviceDrawerClick submit callback and the
 * study.
 * @returns An object carrying the drawer state and setter, the user, CardioOne and web-view flags,
 * the device model list and default, the device select refs, and the selection, modal and assign
 * handlers.
 */
export const useAssignDeviceDrawer = ({
  visible,
  handleAssignDeviceDrawerClick,
  study,
}: Pick<AssignDeviceDrawerProps, 'visible' | 'handleAssignDeviceDrawerClick' | 'study'>) => {
  /**
   * The signed-in user record read from the user store. Selected through useUserStore so the drawer can inspect the roles of the user.
   *
   * @returns The current user object held in the store.
   */
  const meRedux = useUserStore((s) => s.me);

  /**
   * The page loading-text setter taken from the loading store. Selected through useLoadingStore so the drawer can raise and clear the global loading indicator.
   *
   * @returns The setLoadingText action from the loading store.
   */
  const loadingPageAction = useLoadingStore((s) => s.setLoadingText);

  const isCardioOne = auth.userOrganization()?.slug === ORGANIZATION_SLUG.CARDIO_ONE;
  const deviceModelList = isCardioOne ? CARDIO_ONE_DEVICE_MODEL_LIST : DEVICE_MODEL_LIST;
  const defaultDeviceModel = isCardioOne ? DEVICE.BIOTRES_PRO : DEVICE_MODEL_LIST[0].value;

  const [state, setState] = useMergeState<AssignDeviceDrawerState>({
    model: defaultDeviceModel,
    searchDeviceId: '',
    isSearchingDevice: false,
    isDefaultDeviceOptions: true,
    isLoadingDevice: false,
    devices: [],
    devicesOptions: [],
    device: null,
    errorObject: null,
    isOpenDeviceErrorModal: false,
    deviceErrorModalMessage: '',
    isOpenWarningAutoEventModal: false,
    isOpenWarningModal: false,
    isOpenReturnedDeviceModal: false,
    isSavingReturnedDeviceModal: false,
    returnedDeviceDataModal: null,
    isOpenProtectPlusDeviceModal: false,
    isAssigningDevice: false,
  });

  const fetchDeviceTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasClosedDeviceIdDropdown = useRef(false);
  const hasChangedDevice = useRef(false);
  const deviceIdSelectRef = useRef<{ focusAndOpenSelectInput: () => void } | null>(null);

  const isWebView = isWebViewCreateStudy();

  /**
   * Loads the default list of online, unassigned devices offered when assigning a device to a study. Queries call-center devices for the study's facility and the selected model with a 100-item limit, drops every device already running a study, converts the rest into select options flagged for test facilities, and logs the failure and returns empty lists when the query throws.
   *
   * @returns The loaded devices and their select options, both empty on failure.
   */
  const handleGetDefaultDevices = async () => {
    try {
      const queryFilter = {
        isInStudy: false,
        status: DEVICE_STATUS.ONLINE,
        sortBy: 'deviceId',
        sortOrder: 'desc',
        facilityId: study.facility.id,
        model: state.model,
        isRenewal: false,
        additionalStatus: 'Active',
      };
      const devices = _.compact(await fetchCallCenterDevices(queryFilter, 100));
      _.remove(devices, (x) => !!x.isRunningStudy);
      const devicesOptions = handleConvertDeviceData({
        devices,
        isFacilityTest: study.facility.status === 'Test',
      });
      return {
        devices,
        devicesOptions,
      };
    } catch (error) {
      console.error('Failed to get default devices: ', error);
      return {
        devices: [],
        devicesOptions: [],
      };
    }
  };

  /**
   * Refreshes the assign-device drawer with the default device list. Raises the loading flag, awaits the online unassigned devices and their select options, then stores both in state and clears the flag.
   *
   * @returns A promise resolving once the devices have been stored.
   */
  const handleDefaultDevicesLoad = async () => {
    setState({ isLoadingDevice: true });
    const { devices, devicesOptions } = await handleGetDefaultDevices();
    setState({
      isLoadingDevice: false,
      devices,
      devicesOptions,
    });
  };

  /**
   * Loads the devices matching a typed device ID for the study's facility. Clears the current selection and both lists, queries call-center devices filtered by facility, model and device ID with a 100-item limit, converts them into select options flagged for test facilities, and on failure logs the error and only drops the searching flag.
   *
   * @param deviceId - Device ID to filter the query by.
   * @returns A promise resolving once the matching devices have been stored.
   */
  const handleDevicesLoad = async (deviceId: string) => {
    try {
      setState({
        device: null,
        devices: [],
        devicesOptions: [],
      });

      const queryFilter = {
        sortBy: 'deviceId',
        sortOrder: 'desc',
        facilityId: study.facility.id,
        model: state.model,
        deviceId,
      };
      const devices = _.compact(await fetchCallCenterDevices(queryFilter, 100));
      const devicesOptions = handleConvertDeviceData({
        devices,
        isFacilityTest: study.facility.status === 'Test',
      });

      setState({
        isSearchingDevice: false,
        devices,
        devicesOptions,
      });
    } catch (error) {
      console.error('Failed to fetch devices: ', error);
      setState({ isSearchingDevice: false });
    }
  };

  /**
   * Applies a device model change in the assign-device drawer. Stores the new model and discards everything derived from the previous one: the selected device, the typed device ID and both the device list and its options.
   *
   * @param name - Name of the changed field, not read.
   * @param value - Newly selected device model.
   * @returns Nothing.
   */
  const handleDeviceTypeChange = (name: string, value: string) => {
    setState({
      model: value,
      device: null,
      searchDeviceId: '',
      devices: [],
      devicesOptions: [],
    });
  };

  /**
   * Tracks whether the device ID dropdown in the assign-device drawer has just been closed. Writes the negation of the open flag into the hasClosedDeviceIdDropdown ref so the search callback fired by closing it can be ignored.
   *
   * @param open - True when the dropdown is opening, false when it is closing.
   * @returns Nothing.
   */
  const handleDeviceIdDropdownVisibleChange = (open: boolean) => {
    hasClosedDeviceIdDropdown.current = !open;
  };

  /**
   * Starts a device ID search typed into the assign-device drawer. Swallows the single search event caused by closing the dropdown or by picking a device and resets those refs, otherwise marks the search in progress, leaves the default option list, stores the typed ID and clears the current selection.
   *
   * @param searchDeviceId - Device ID text typed into the select.
   * @returns Nothing.
   */
  const handleDeviceIdSearch = (searchDeviceId: string) => {
    if (hasClosedDeviceIdDropdown.current) {
      hasClosedDeviceIdDropdown.current = false;
      return;
    }

    if (hasChangedDevice.current) {
      hasChangedDevice.current = false;
      return;
    }

    setState({
      isSearchingDevice: true,
      isDefaultDeviceOptions: false,
      searchDeviceId,
      device: null,
    });
  };

  /**
   * Validates and applies the device chosen in the assign-device drawer. Shows the page loader, then bails out with a modal for a device error, for a Biotres device that cannot run MCT or whose firmware is too old on non-Holter studies, or for a Protect Plus device; otherwise it fetches any pending patient return for the device and either opens the returned-device modal or stores the option as the selection, toasting a failed check.
   *
   * @param name - Name of the changed field, not read.
   * @param value - Selected device ID, not read.
   * @param option - Selected device option carrying its model, firmware, pricing and Protect Plus flags.
   * @returns A promise resolving once the selection or a blocking modal has been applied.
   */
  const handleDeviceChange = async (name: string, value: string, option: DeviceOption) => {
    hasChangedDevice.current = true;

    loadingPageAction(LOADING_TYPES.LOADING);

    const { isOpenDeviceErrorModal, deviceErrorModalMessage } = checkDeviceError(option);
    if (isOpenDeviceErrorModal) {
      setState({
        isOpenDeviceErrorModal,
        deviceErrorModalMessage,
      });
      loadingPageAction();
      return;
    }

    if (option.model === DEVICE.BIOTRES) {
      const studyCondition = ![STUDY_TYPES.HOLTER, STUDY_TYPES.EXTENDED_HOLTER].includes(
        study.studyType,
      );
      if (studyCondition && !option?.canRunBiotresMCT && study.facility.status !== 'Test') {
        setState({ isOpenWarningAutoEventModal: true });
        loadingPageAction();
        return;
      }

      if (studyCondition && checkCanShowModal(option.lastSync.schema)) {
        setState({ isOpenWarningModal: true });
        loadingPageAction();
        return;
      }
    }

    if (option.isProtectPlus) {
      setState({ isOpenProtectPlusDeviceModal: true });
      loadingPageAction();
      return;
    }

    try {
      const sentDevicePatientReturned = await fetchSentDevicePatientReturned(option.value);
      if (!_.isEmpty(sentDevicePatientReturned)) {
        const isClinicRole =
          _.intersection(meRedux.roles, [ROLE.CLINIC_TECHNICIAN, ROLE.CLINIC_PHYSICIAN]).length > 0;
        const isViewGotoFleetManagement = isClinicRole && !isWebView;
        setState({
          isOpenReturnedDeviceModal: true,
          returnedDeviceDataModal: {
            ...option,
            isViewGotoFleetManagement,
            patientReturnId: sentDevicePatientReturned?.id,
            studyId: sentDevicePatientReturned?.study?.id,
          },
        });
        loadingPageAction();
        return;
      }

      setState({
        device: option,
        searchDeviceId: option.value,
      });
    } catch (error) {
      console.error('Failed to fetch sent device patient returned: ', error);
      showMessageError({ content: t`Failed to check device ID` });
    } finally {
      loadingPageAction();
    }
  };

  /**
   * Flips the device error modal of the assign-device drawer. Inverts isOpenDeviceErrorModal and clears the stored error message text.
   *
   * @returns Nothing.
   */
  const toggleDisplayDeviceErrorModal = () => {
    setState({
      isOpenDeviceErrorModal: !state.isOpenDeviceErrorModal,
      deviceErrorModalMessage: '',
    });
  };

  /**
   * Handles the right button of the assign-device error modal. Toggles the modal and, when the error
   * followed a device change, clears that marker and reopens the device-ID select with focus.
   *
   * @returns Nothing.
   */
  const handleRightButtonClickDeviceErrorModal: () => void = () => {
    toggleDisplayDeviceErrorModal();
    if (hasChangedDevice.current) {
      hasChangedDevice.current = false;
      if (deviceIdSelectRef.current) {
        deviceIdSelectRef.current.focusAndOpenSelectInput();
      }
    }
  };

  /**
   * Flips one of the two assign-device warning modals. Targets isOpenWarningModal for the firmware type and isOpenWarningAutoEventModal for anything else, inverting the chosen flag and merging it into state.
   *
   * @param type - Warning kind, firmware for the firmware modal.
   * @returns Nothing.
   */
  const toggleDisplayWarningModal = (type: string) => {
    const newStateModal =
      type === 'firmware'
        ? { isOpenWarningModal: !state.isOpenWarningModal }
        : { isOpenWarningAutoEventModal: !state.isOpenWarningAutoEventModal };
    setState({ ...newStateModal });
  };

  /**
   * Dismisses the Protect Plus warning raised when such a device is picked. Sets isOpenProtectPlusDeviceModal to false.
   *
   * @returns Nothing.
   */
  const handleProtectPlusDeviceModalClose = () => {
    setState({ isOpenProtectPlusDeviceModal: false });
  };

  /**
   * Dismisses the returned-device confirmation without marking the device returned. Sets isOpenReturnedDeviceModal to false.
   *
   * @returns Nothing.
   */
  const onClickCancelReturnedDeviceModal = () => {
    setState({ isOpenReturnedDeviceModal: false });
  };

  /**
   * Confirms that the patient returned the device before it is assigned. Marks the pending patient return as returned by its ID, then strips the modal-only fields off the stored device data and promotes it to the current selection while closing the modal; a failure logs, toasts an error and closes the modal with the stored data cleared.
   *
   * @returns A promise resolving once the return has been marked or the failure handled.
   */
  const onClickConfirmReturnedDeviceModal = async () => {
    try {
      setState({ isSavingReturnedDeviceModal: true });
      const input = {
        ids: [state.returnedDeviceDataModal?.patientReturnId],
      };
      await handleMarkDevicePatientReturned(input);
      const option = _.omit(_.cloneDeep(state.returnedDeviceDataModal), [
        'isViewGotoFleetManagement',
        'patientReturnId',
        'studyId',
      ]) as any;
      setState({
        device: option,
        searchDeviceId: option.value,
        isOpenReturnedDeviceModal: false,
        isSavingReturnedDeviceModal: false,
        returnedDeviceDataModal: null,
      });
    } catch (error) {
      console.error('Failed to mark device patient returned: ', error);
      showMessageError({ content: t`Failed to mark device patient returned` });
      setState({
        isOpenReturnedDeviceModal: false,
        isSavingReturnedDeviceModal: false,
        returnedDeviceDataModal: null,
      });
    }
  };

  /**
   * Ends the in-progress state of a device assignment once the parent has handled it. Sets
   * isAssigningDevice to false.
   *
   * @returns Nothing.
   */
  const callbackAssignDevice = () => {
    setState({ isAssigningDevice: false });
  };

  /**
   * Submits the assign-device request for the study. Clones the study setting and overrides its algAmp with the ECG amplification resolved from the facility setting and the selected model, marks the assignment in progress, then calls handleAssignDeviceDrawerClick with the study ID, selected device ID, model and setting plus the completion callback.
   *
   * @returns Nothing.
   */
  const handleAssignClick = () => {
    const clonedStudySetting = _.cloneDeep(study.info?.studySetting);
    if (clonedStudySetting) {
      const ampEcgSignal = getValueAmpEcg(study.facility.amplifyEcgSignal, state.model);
      clonedStudySetting.algAmp = ampEcgSignal;
    }
    setState({ isAssigningDevice: true });
    handleAssignDeviceDrawerClick(
      {
        studyId: study.id,
        // the Assign button is disabled while state.device is null
        deviceId: state.device!.value,
        deviceType: state.model,
        studySetting: clonedStudySetting,
      },
      callbackAssignDevice,
    );
  };

  /**
   * Applies a study pricing update to the devices listed in the assign-device drawer. When the message carries applied device IDs it locates the first matching loaded device, clones the list to set its canRunBiotresMCT from the message's autoEventDetection flag, rebuilds the select options for the facility's test status, re-resolves the current selection against them and closes the auto-event warning modal.
   *
   * @param msg - Socket message carrying the applied device IDs and the auto event detection flag.
   * @returns Nothing.
   */
  const handleStudyPricingUpdatedSocket = (msg: StudyPricingUpdatedSocketMessage) => {
    if (msg.appliedDeviceIds) {
      /**
       * Index of the loaded device affected by a pricing update. Found with a lodash index lookup over the devices in state, matching those whose device ID appears in the applied device IDs of the message.
       *
       * @returns Index of the matching device, or -1 when none matches.
       */
      const needUpdatedDevice = _.findIndex(state.devices, (device) =>
        msg.appliedDeviceIds.includes(device.deviceId),
      );
      if (needUpdatedDevice >= 0) {
        const clonedDevices = _.cloneDeep(state.devices);
        clonedDevices[needUpdatedDevice].canRunBiotresMCT = !!msg.autoEventDetection;

        const isFacilityTest = study.facility.status === 'Test';
        const devicesOptions = handleConvertDeviceData({
          devices: clonedDevices,
          isFacilityTest,
        });

        let clonedDevice = _.cloneDeep(state.device);
        if (clonedDevice && clonedDevice.value === clonedDevices[needUpdatedDevice].deviceId) {
          const clonedDeviceValue = clonedDevice.value;
          clonedDevice =
            devicesOptions.find((device) => device.value === clonedDeviceValue) ?? null;
        }

        setState({
          devices: clonedDevices,
          devicesOptions,
          device: clonedDevice,
          isOpenWarningAutoEventModal: false,
        });
      }
    }
  };

  /**
   * Reacts to a device status event while the assign-device drawer is open. Returns early when the drawer is hidden; on the default option list it reloads the devices and re-resolves the selected device against the fresh options, clearing the selection when it disappeared, and otherwise clears the selection only when the event names the selected device and reports it offline or already assigned.
   *
   * @param msg - Socket message carrying the device ID, its status and whether it is assigned.
   * @returns A promise resolving once the drawer state has been reconciled.
   */
  const handleDeviceEventSocket = async (msg: DeviceEventSocketMessage) => {
    if (!visible) {
      return;
    }

    if (state.isDefaultDeviceOptions) {
      const setStateObject = {};

      const { devices, devicesOptions } = await handleGetDefaultDevices();
      Object.assign(setStateObject, {
        devices,
        devicesOptions,
      });

      if (state.device) {
        /**
         * The device option that matches the currently selected device. Looked up in the freshly loaded device options by comparing each option value against the selected device value.
         *
         * @returns The matching device option, or undefined when the selection is gone.
         */
        const currentDeviceValue = state.device.value;
        const foundDevice = devicesOptions.find(
          (deviceOption) => deviceOption.value === currentDeviceValue,
        );
        if (foundDevice) {
          _.assign(setStateObject, {
            device: foundDevice,
            searchDeviceId: foundDevice.value,
          });
        } else {
          _.assign(setStateObject, {
            device: null,
            searchDeviceId: '',
          });
        }
      }

      setState(setStateObject);
    } else if (state.device) {
      const shouldUpdate = msg.deviceId === state.device.value;
      if (!shouldUpdate) {
        return;
      }

      // *: `msg.assigned` is `true` when the device is assigned to a study
      if (msg.status === DEVICE_STATUS.OFFLINE || msg.assigned) {
        setState({
          device: null,
          searchDeviceId: '',
        });
      }
    }
  };

  useEffect(() => {
    if (visible) {
      handleDefaultDevicesLoad();
    } else {
      setState({
        model: defaultDeviceModel,
        device: null,
        searchDeviceId: '',
        devices: [],
        devicesOptions: [],
      });
    }
  }, [visible]);

  useUpdateEffect(() => {
    if (visible) {
      handleDefaultDevicesLoad();
    }
  }, [state.model]);

  useUpdateEffect(() => {
    if (fetchDeviceTimeout.current) {
      clearTimeout(fetchDeviceTimeout.current);
    }

    if (!state.searchDeviceId) {
      setState({
        isSearchingDevice: false,
        devices: [],
        devicesOptions: [],
      });
      return;
    }

    if (hasChangedDevice.current) {
      hasChangedDevice.current = false;
      return;
    }

    fetchDeviceTimeout.current = setTimeout(async () => {
      await handleDevicesLoad(state.searchDeviceId);
    }, 500);
  }, [state.searchDeviceId]);

  useEmitter(EMITTER_CONSTANTS.STUDY_PRICING_UPDATED, handleStudyPricingUpdatedSocket, [
    state.devices,
    state.device,
  ]);

  useEmitter(EMITTER_CONSTANTS.DEVICE_EVENT, handleDeviceEventSocket, [
    visible,
    study?.facility,
    state.model,
    state.device,
    state.isDefaultDeviceOptions,
  ]);

  return {
    state,
    setState,
    meRedux,
    isCardioOne,
    deviceModelList,
    defaultDeviceModel,
    isWebView,
    deviceIdSelectRef,
    hasChangedDevice,
    handleDeviceTypeChange,
    handleDeviceIdDropdownVisibleChange,
    handleDeviceIdSearch,
    handleDeviceChange,
    toggleDisplayDeviceErrorModal,
    handleRightButtonClickDeviceErrorModal,
    toggleDisplayWarningModal,
    handleProtectPlusDeviceModalClose,
    onClickCancelReturnedDeviceModal,
    onClickConfirmReturnedDeviceModal,
    handleAssignClick,
  };
};
